import React from 'react';
import { BarChart, Trash2, Calculator } from 'lucide-react';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для сравнения сохраненных расчетов налога
 */
const ComparisonSidebar = ({ 
  savedCalculations, 
  deleteSavedCalculation, 
  loadSavedCalculation,
  propertyTypes 
}) => {
  // Если нет сохраненных расчетов
  if (!savedCalculations || savedCalculations.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение расчетов
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Здесь будут отображаться сохраненные расчеты для сравнения. Нажмите «Сохранить расчет» после выполнения расчета.
        </p>
      </div>
    );
  }

  // Получаем название типа имущества
  const getPropertyTypeName = (propertyTypeId) => {
    const foundType = propertyTypes.find(type => type.id === propertyTypeId);
    return foundType ? foundType.name : 'Имущество';
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <BarChart className="w-5 h-5 mr-2 text-indigo-500" />
          Сравнение расчетов
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {savedCalculations.length} {pluralize(savedCalculations.length, 'вариант', 'варианта', 'вариантов')}
        </span>
      </div>

      <div className="space-y-4">
        {savedCalculations.map((calc) => (
          <div 
            key={calc.id} 
            className="bg-gray-50 dark:bg-gray-700/30 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {calc.name}
              </h4>
              <div className="flex items-center space-x-2">
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Calculator className="w-4 h-4" />}
                  onClick={() => loadSavedCalculation(calc.id)}
                  title="Загрузить расчет"
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={<Trash2 className="w-4 h-4 text-red-500" />}
                  onClick={() => deleteSavedCalculation(calc.id)}
                  title="Удалить"
                />
              </div>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <div className="flex justify-between mb-1">
                <span>Тип:</span>
                <span>
                  {getPropertyTypeName(calc.formData.propertyType)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Стоимость:</span>
                <span className="font-medium">
                  {formatCurrency(calc.formData.cadastralValue, 0)}
                </span>
              </div>
              <div className="flex justify-between mb-1">
                <span>Площадь:</span>
                <span>
                  {formatNumber(calc.formData.area, 1)} м²
                </span>
              </div>
              <div className="flex justify-between">
                <span>Налог:</span>
                <span className="font-semibold text-indigo-600 dark:text-indigo-400">
                  {formatCurrency(calc.results.finalTax, 0)}
                </span>
              </div>
              <div className="flex justify-between mt-1 text-xs text-gray-500 dark:text-gray-500">
                <span>Регион:</span>
                <span>{calc.results.regionName}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {savedCalculations.length >= 2 && (
        <div className="mt-4 p-3 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg">
          <h4 className="text-sm font-medium text-indigo-700 dark:text-indigo-300 mb-2">Сравнительный анализ</h4>
          
          {/* Находим минимальный и максимальный налог */}
          {(() => {
            const minTax = savedCalculations.reduce((min, calc) => 
              calc.results.finalTax < min.tax ? { tax: calc.results.finalTax, name: calc.name } : min, 
              { tax: Infinity, name: '' }
            );
            
            const maxTax = savedCalculations.reduce((max, calc) => 
              calc.results.finalTax > max.tax ? { tax: calc.results.finalTax, name: calc.name } : max, 
              { tax: -Infinity, name: '' }
            );
            
            return (
              <>
                <div className="flex justify-between text-xs mb-1">
                  <span className="text-green-600 dark:text-green-400">Минимальный налог:</span>
                  <span className="font-medium">{formatCurrency(minTax.tax, 0)}</span>
                </div>
                <div className="flex justify-between text-xs mb-3">
                  <span className="text-red-500 dark:text-red-400">Максимальный налог:</span>
                  <span className="font-medium">{formatCurrency(maxTax.tax, 0)}</span>
                </div>
                <div className="text-xs text-indigo-600 dark:text-indigo-400">
                  Разница: {formatCurrency(maxTax.tax - minTax.tax, 0)} ({formatNumber((maxTax.tax / minTax.tax - 1) * 100, 0)}%)
                </div>
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
};

// Вспомогательная функция для склонения слов
const pluralize = (number, one, few, many) => {
  let n = Math.abs(number);
  n %= 100;
  if (n >= 5 && n <= 20) {
    return many;
  }
  n %= 10;
  if (n === 1) {
    return one;
  }
  if (n >= 2 && n <= 4) {
    return few;
  }
  return many;
};

export default ComparisonSidebar;